// src/services/leadService.js
import { supabase } from './supabase';

export const CRM_STATUS_PIPELINE = [
  'New',
  'Contacted',
  'Interested',
  'Site Visit',
  'Negotiation',
  'Closed Won',
  'Closed Lost'
];

export const CRM_SOURCES = [
  'property enquiry',
  'popup',
  'app',
  'manual'
];

export const CRM_REQUIREMENTS = [
  'Buy a Property',
  'Sell a Property',
  'Rent a Property',
  'New House Construction',
  'Commercial Construction',
  'Renovation & Repair',
  'Architecture & Building Plan',
  'General Inquiry'
];

export const CRM_CITIES = ['Ghazipur', 'Varanasi', 'Lucknow', 'Other'];

export const CRM_CALL_OUTCOMES = [
  { label: 'Interested - Send Details', nextStatus: 'Interested' },
  { label: 'Site Visit Confirmed', nextStatus: 'Site Visit' },
  { label: 'Call Back Later', nextStatus: 'Contacted' },
  { label: 'Ringing / No Answer', nextStatus: 'Contacted' },
  { label: 'Busy / Switched Off', nextStatus: 'Contacted' },
  { label: 'Price Negotiation', nextStatus: 'Negotiation' },
  { label: 'Deal Won / Booking Done', nextStatus: 'Closed Won' },
  { label: 'Not Interested / Lost', nextStatus: 'Closed Lost' }
];

export const leadService = {
  /**
   * Fetch leads from central `public.leads` table with relations
   */
  async getCentralLeads() {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select(`
          *,
          property:properties(id, title, city, price),
          assigned_admin:admin_users!leads_assigned_to_fkey(id, full_name, email, role)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        // Fallback without relation if foreign key names vary
        const fallback = await supabase
          .from('leads')
          .select('*')
          .order('created_at', { ascending: false });
        return fallback;
      }

      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error getting central leads:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Check if a lead with this mobile number already exists (duplicate check)
   */
  async checkDuplicateMobile(mobile, excludeId = null) {
    if (!mobile) return { isDuplicate: false, existingLead: null };
    const clean = String(mobile).replace(/\D/g, '').slice(-10);
    if (clean.length < 10) return { isDuplicate: false, existingLead: null };

    try {
      // Try stored procedure
      const { data, error } = await supabase.rpc('check_duplicate_lead_mobile', {
        p_mobile: clean,
        p_exclude_id: excludeId || null
      });

      if (!error && data && data.length > 0) {
        return { isDuplicate: true, existingLead: data[0] };
      }
    } catch {
      // Fallback direct query below
    }

    try {
      const { data } = await supabase
        .from('leads')
        .select('id, name, mobile, status, created_at')
        .eq('mobile', clean)
        .order('created_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0 && (!excludeId || data[0].id !== excludeId)) {
        return { isDuplicate: true, existingLead: data[0] };
      }
      return { isDuplicate: false, existingLead: null };
    } catch (err) {
      console.error('Duplicate check error:', err);
      return { isDuplicate: false, existingLead: null };
    }
  },

  /**
   * Create a lead manually via stored procedure with activity logging
   */
  async createManualLead(leadData) {
    try {
      const { data, error } = await supabase.rpc('create_crm_lead', {
        p_name: leadData.name.trim(),
        p_mobile: leadData.mobile.replace(/\D/g, '').slice(-10),
        p_email: leadData.email ? leadData.email.trim() : null,
        p_source: leadData.source || 'manual',
        p_requirement: leadData.requirement || 'General Inquiry',
        p_city: leadData.city || 'Ghazipur',
        p_property_type: leadData.property_type || null,
        p_budget: leadData.budget || null,
        p_property_id: leadData.property_id || null,
        p_assigned_to: leadData.assigned_to || null,
        p_priority: leadData.priority || 'Warm',
        p_notes: leadData.notes ? leadData.notes.trim() : null
      });

      if (!error && data?.success) {
        return { data, error: null };
      }
      if (error) throw error;
    } catch (err) {
      // Fallback direct insert if RPC not run yet
      console.warn('Falling back to direct insert for lead:', err.message);
      try {
        const cleanMobile = leadData.mobile.replace(/\D/g, '').slice(-10);
        const record = {
          name: leadData.name.trim(),
          mobile: cleanMobile,
          email: leadData.email ? leadData.email.trim() : null,
          source: leadData.source || 'manual',
          requirement: leadData.requirement || 'General Inquiry',
          city: leadData.city || 'Ghazipur',
          property_type: leadData.property_type || null,
          budget: leadData.budget || null,
          property_id: leadData.property_id || null,
          assigned_to: leadData.assigned_to || null,
          priority: leadData.priority || 'Warm',
          notes: leadData.notes ? leadData.notes.trim() : null,
          status: 'New',
          created_at: new Date().toISOString()
        };

        const { data: inserted, error: insertErr } = await supabase
          .from('leads')
          .insert([record])
          .select('id')
          .single();

        if (insertErr) throw insertErr;
        return { data: { success: true, lead_id: inserted.id }, error: null };
      } catch (fallbackErr) {
        return { data: null, error: fallbackErr };
      }
    }
  },

  /**
   * Update lead details (status, priority, assigned_to) with activity history tracking
   */
  async updateCrmLead(leadId, updates) {
    try {
      const { data, error } = await supabase.rpc('update_crm_lead', {
        p_lead_id: leadId,
        p_status: updates.status || null,
        p_priority: updates.priority || null,
        p_assigned_to: updates.assigned_to || null,
        p_note: updates.note || null,
        p_activity_type: updates.activity_type || 'status_change'
      });

      if (!error && data?.success) {
        return { data, error: null };
      }
      if (error) throw error;
    } catch {
      // Direct update fallback
      try {
        const updatePayload = {};
        if (updates.status) updatePayload.status = updates.status;
        if (updates.priority) updatePayload.priority = updates.priority;
        if (updates.assigned_to !== undefined) updatePayload.assigned_to = updates.assigned_to || null;
        updatePayload.updated_at = new Date().toISOString();

        const { data, error } = await supabase
          .from('leads')
          .update(updatePayload)
          .eq('id', leadId)
          .select('id');

        return { data, error };
      } catch (err) {
        return { data: null, error: err };
      }
    }
  },

  /**
   * Get activity history for a specific lead ("kisne kab kya badla")
   */
  async getLeadActivities(leadId) {
    try {
      const { data, error } = await supabase
        .from('lead_activities')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error getting lead activities:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Add a note to lead activity history
   */
  async addLeadActivityNote(leadId, noteText, activityType = 'note') {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUserId = sessionData?.session?.user?.id;

      let adminName = 'Staff';
      if (currentUserId) {
        const { data: adminUser } = await supabase
          .from('admin_users')
          .select('full_name')
          .eq('id', currentUserId)
          .single();
        if (adminUser?.full_name) adminName = adminUser.full_name;
      }

      const { data, error } = await supabase
        .from('lead_activities')
        .insert([{
          lead_id: leadId,
          performed_by: currentUserId || null,
          performer_name: adminName,
          activity_type: activityType,
          field_changed: 'notes',
          notes: noteText.trim(),
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error adding activity note:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Fetch approved properties list for linking to leads
   */
  async getPropertiesForLead() {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title, city, price, property_type')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err };
    }
  },

  /**
   * Auto Round-Robin Lead Assignment (single lead or batch unassigned)
   */
  async assignRoundRobin(leadId = null) {
    try {
      const { data, error } = await supabase.rpc('crm_assign_lead_round_robin', {
        p_lead_id: leadId || null
      });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error in round robin assignment:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Log Call Outcome with Notes and Next Follow-up Schedule
   */
  async logCallOutcome(leadId, callData) {
    try {
      const { data, error } = await supabase.rpc('log_crm_call_outcome', {
        p_lead_id: leadId,
        p_call_outcome: callData.outcome,
        p_notes: callData.notes || null,
        p_next_status: callData.next_status || null,
        p_follow_up_date: callData.follow_up_date || null
      });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      // Fallback: add note and update lead
      try {
        await this.addLeadActivityNote(leadId, `[${callData.outcome}] ${callData.notes || ''}`, 'call');
        if (callData.next_status) {
          await this.updateCrmLead(leadId, { status: callData.next_status });
        }
        return { data: { success: true }, error: null };
      } catch (fallbackErr) {
        return { data: null, error: fallbackErr };
      }
    }
  },

  /**
   * Get Scheduled Follow-up Reminders (Today & Overdue)
   */
  async getFollowupReminders(staffId = null) {
    try {
      const { data, error } = await supabase.rpc('get_crm_followup_reminders', {
        p_staff_id: staffId || null
      });

      if (!error && data) {
        return { data, error: null };
      }
    } catch {
      // Fallback direct query below
    }

    try {
      let query = supabase
        .from('crm_lead_followups')
        .select('*')
        .eq('is_completed', false)
        .order('due_date', { ascending: true })
        .limit(50);

      if (staffId) {
        query = query.eq('assigned_to', staffId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const now = new Date();
      const enriched = (data || []).map(f => ({
        followup_id: f.id,
        lead_id: f.lead_id,
        task_title: f.task_title,
        due_date: f.due_date,
        is_completed: f.is_completed,
        is_overdue: new Date(f.due_date) < now,
        assigned_to: f.assigned_to
      }));

      return { data: enriched, error: null };
    } catch (err) {
      console.error('Error getting followup reminders:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Mark a scheduled follow-up reminder as completed
   */
  async completeFollowupTask(followupId) {
    try {
      const { data, error } = await supabase
        .from('crm_lead_followups')
        .update({
          is_completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', followupId);

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Error completing followup task:', err);
      return { data: null, error: err };
    }
  },

  /**
   * Existing helper methods preserved
   */
  async getLeads() {
    return await supabase
      .from('enquiries')
      .select(`
        *,
        property:properties(id, title, city),
        buyer:users!enquiries_buyer_id_fkey(id, full_name, phone_number),
        seller:users!enquiries_seller_id_fkey(id, full_name, phone_number)
      `)
      .order('created_at', { ascending: false });
  },

  async updateLeadStatus(leadId, nextStatus) {
    const { data, error } = await supabase
      .from('enquiries')
      .update({ status: nextStatus })
      .eq('id', leadId)
      .select('id');
    return { data, error };
  },

  async getServiceLeads() {
    return await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
  },

  async updateServiceLeadStatus(leadId, nextStatus) {
    const { data, error } = await supabase
      .from('leads')
      .update({ status: nextStatus })
      .eq('id', leadId)
      .select('id');
    return { data, error };
  },

  async getAdmins() {
    return await supabase
      .from('admin_users')
      .select('id, full_name, email, role')
      .eq('status', 'Active')
      .order('full_name');
  },

  async assignServiceLead(leadId, adminId) {
    const { data, error } = await supabase
      .from('leads')
      .update({ assigned_to: adminId || null })
      .eq('id', leadId)
      .select('id');
    return { data, error };
  },

  async getSolutionLeads() {
    return await supabase
      .from('property_solution_leads')
      .select('*')
      .order('created_at', { ascending: false });
  },

  async updateSolutionLeadStatus(leadId, nextStatus) {
    const { data, error } = await supabase
      .from('property_solution_leads')
      .update({ status: nextStatus, updated_at: new Date().toISOString() })
      .eq('id', leadId)
      .select('id');
    return { data, error };
  },

  async deleteSolutionLead(leadId) {
    return await supabase
      .from('property_solution_leads')
      .delete()
      .eq('id', leadId);
  },

  /**
   * CRM Phase 3: Staff Performance Report
   */
  async getCrmStaffPerformanceReport(fromDate = null, toDate = null) {
    try {
      const { data, error } = await supabase.rpc('get_crm_staff_performance_report', {
        p_from_date: fromDate ? new Date(fromDate).toISOString() : null,
        p_to_date: toDate ? new Date(toDate).toISOString() : null
      });

      if (!error && data && data.length > 0) {
        return { data, error: null };
      }
    } catch {
      // Fallback to client-side aggregation below
    }

    try {
      const [leadsRes, staffRes] = await Promise.all([
        this.getCentralLeads(),
        this.getAdmins()
      ]);

      const staffList = (staffRes?.data || []).filter(s =>
        s.status === 'Active' && ['Sales Executive', 'Admin', 'Super Admin'].includes(s.role)
      );

      let leads = leadsRes?.data || [];
      if (fromDate) leads = leads.filter(l => new Date(l.created_at) >= new Date(fromDate));
      if (toDate) leads = leads.filter(l => new Date(l.created_at) <= new Date(toDate));

      const report = staffList.map(s => {
        const staffLeads = leads.filter(l => l.assigned_to === s.id);
        const total_assigned = staffLeads.length;
        const active_leads = staffLeads.filter(l => !['Closed Won', 'Closed Lost'].includes(l.status)).length;
        const site_visits = staffLeads.filter(l => l.status === 'Site Visit').length;
        const deals_won = staffLeads.filter(l => l.status === 'Closed Won').length;
        const deals_lost = staffLeads.filter(l => l.status === 'Closed Lost').length;
        const conversion_rate_pct = total_assigned > 0
          ? Math.round((deals_won / total_assigned) * 1000) / 10
          : 0;

        return {
          staff_id: s.id,
          staff_name: s.full_name,
          role: s.role,
          total_assigned,
          active_leads,
          site_visits,
          deals_won,
          deals_lost,
          conversion_rate_pct
        };
      });

      report.sort((a, b) => b.deals_won - a.deals_won || b.total_assigned - a.total_assigned);
      return { data: report, error: null };
    } catch (err) {
      console.error('Error calculating staff performance:', err);
      return { data: [], error: err };
    }
  },

  /**
   * CRM Phase 3: Conversion Funnel Report
   */
  async getCrmConversionFunnel(fromDate = null, toDate = null) {
    try {
      const { data, error } = await supabase.rpc('get_crm_conversion_funnel', {
        p_from_date: fromDate ? new Date(fromDate).toISOString() : null,
        p_to_date: toDate ? new Date(toDate).toISOString() : null
      });

      if (!error && data && data.length > 0) {
        return { data, error: null };
      }
    } catch {
      // Fallback
    }

    try {
      const leadsRes = await this.getCentralLeads();
      let leads = leadsRes?.data || [];
      if (fromDate) leads = leads.filter(l => new Date(l.created_at) >= new Date(fromDate));
      if (toDate) leads = leads.filter(l => new Date(l.created_at) <= new Date(toDate));

      const total = leads.length || 1;
      const stages = [
        { stage: 'New', stage_order: 1 },
        { stage: 'Contacted', stage_order: 2 },
        { stage: 'Interested', stage_order: 3 },
        { stage: 'Site Visit', stage_order: 4 },
        { stage: 'Negotiation', stage_order: 5 },
        { stage: 'Closed Won', stage_order: 6 },
        { stage: 'Closed Lost', stage_order: 7 }
      ];

      const funnel = stages.map(st => {
        const count = leads.filter(l => (l.status || 'New') === st.stage).length;
        return {
          stage: st.stage,
          stage_order: st.stage_order,
          leads_count: count,
          pct_of_total: Math.round((count / total) * 1000) / 10
        };
      });

      return { data: funnel, error: null };
    } catch (err) {
      return { data: [], error: err };
    }
  },

  /**
   * CRM Phase 3: Lead Routing Settings (Get & Update)
   */
  async getLeadRoutingSettings() {
    try {
      // Try crm_settings first
      const { data: crmData } = await supabase
        .from('crm_settings')
        .select('*')
        .eq('key', 'lead_routing')
        .maybeSingle();

      if (crmData?.value) {
        return { data: crmData.value, error: null };
      }

      // Fallback to system_settings
      const { data: sysData } = await supabase
        .from('system_settings')
        .select('*')
        .eq('key', 'lead_routing')
        .maybeSingle();

      const val = sysData?.value || {
        property_enquiry_routing: sysData?.value?.route_to || 'lister',
        popup_lead_routing: sysData?.value?.popup_route_to || 'round_robin',
        popup_lead_staff_id: null
      };

      return { data: val, error: null };
    } catch (err) {
      return {
        data: {
          property_enquiry_routing: 'lister',
          popup_lead_routing: 'round_robin',
          popup_lead_staff_id: null
        },
        error: err
      };
    }
  },

  async updateLeadRoutingSettings(settings) {
    try {
      await Promise.all([
        supabase.from('crm_settings').upsert({
          key: 'lead_routing',
          value: settings,
          updated_at: new Date().toISOString()
        }, { onConflict: 'key' }),
        supabase.from('system_settings').upsert({
          key: 'lead_routing',
          value: {
            route_to: settings.property_enquiry_routing || 'lister',
            popup_route_to: settings.popup_lead_routing || 'round_robin',
            ...settings
          },
          updated_at: new Date().toISOString()
        }, { onConflict: 'key' })
      ]);

      return { success: true, error: null };
    } catch (err) {
      console.error('Error updating routing settings:', err);
      return { success: false, error: err };
    }
  },

  /**
   * Fetch active properties matching a generic Buy/Rent lead's city & type to suggest owners
   */
  async getMatchingPropertiesForLead(city, propertyType) {
    try {
      let query = supabase
        .from('properties')
        .select(`
          id,
          title,
          city,
          property_type,
          price,
          owner_id,
          owner:users!properties_owner_id_fkey(id, full_name, phone_number, email, status)
        `)
        .eq('status', 'approved')
        .not('owner_id', 'is', null);

      if (city && city !== 'Other') {
        query = query.ilike('city', `%${city}%`);
      }

      if (propertyType) {
        query = query.ilike('property_type', `%${propertyType}%`);
      }

      const { data, error } = await query.limit(20);
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error getting matching properties:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Forward a generic Buy/Rent lead to selected property owners (Rule 4)
   */
  async forwardLeadToOwners(leadId, propertyIds, adminId) {
    try {
      // 1. Try RPC forward_lead_to_owners
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('forward_lead_to_owners', {
          p_lead_id: leadId,
          p_property_ids: propertyIds,
          p_admin_id: adminId
        });
        if (!rpcError && rpcData?.success) {
          return { success: true, count: rpcData.forwarded_count, error: null };
        }
      } catch (e) {
        console.warn('RPC forward_lead_to_owners fallback:', e);
      }

      // 2. Direct fallback
      const { data: leadData } = await supabase
        .from('leads')
        .select('*')
        .eq('id', leadId)
        .single();

      if (!leadData) throw new Error('Lead not found');

      const { data: properties } = await supabase
        .from('properties')
        .select('id, title, owner_id')
        .in('id', propertyIds);

      let forwardedCount = 0;
      for (const prop of properties || []) {
        if (!prop.owner_id) continue;
        await supabase.from('enquiries').insert({
          property_id: prop.id,
          seller_id: prop.owner_id,
          buyer_name: leadData.name,
          buyer_phone: leadData.mobile,
          message: `Forwarded by Admin: Interested in ${leadData.requirement} in ${leadData.city} (Budget: ${leadData.budget || 'Flexible'}).`,
          source: 'admin_forwarded',
          status: 'New',
          consent_given: true,
          forwarded_from_lead_id: leadId
        });
        forwardedCount++;
      }

      return { success: true, count: forwardedCount, error: null };
    } catch (err) {
      console.error('Error forwarding lead to owners:', err);
      return { success: false, error: err.message || err };
    }
  }
};

export const ENQUIRY_STATUSES = [
  { value: 'pending', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'visit_scheduled', label: 'Visit scheduled' },
  { value: 'closed', label: 'Closed' },
  { value: 'not_interested', label: 'Not interested' }
];

export const SERVICE_LEAD_STATUSES = ['New', 'Contacted', 'Closed', 'Not interested'];
export const SOLUTION_LEAD_STATUSES = ['New', 'Contacted', 'Closed'];

export const getLeadStatusLabel = (status) => {
  switch ((status || '').toLowerCase()) {
    case '':
    case 'pending':
    case 'new':
      return 'New';
    case 'accepted':
    case 'contacted':
      return 'Contacted';
    case 'interested':
      return 'Interested';
    case 'visit_scheduled':
    case 'site visit':
    case 'visit scheduled':
      return 'Site Visit';
    case 'negotiation':
      return 'Negotiation';
    case 'closed':
    case 'closed won':
      return 'Closed Won';
    case 'rejected':
    case 'not_interested':
    case 'not interested':
    case 'closed lost':
      return 'Closed Lost';
    default:
      return status;
  }
};

export const isStaleLead = (lead) =>
  getLeadStatusLabel(lead.status) === 'New' &&
  Date.now() - new Date(lead.created_at).getTime() > 24 * 60 * 60 * 1000;
